<script setup>
// Chips horizontales de sucursal para celular (Victor 2026-06-02 · Propuesta 3).
// Fila deslizable bajo el header de la lista; un toque cambia de sucursal al
// instante. Solo en celular (< 768px) y solo si hay más de una sucursal
// accesible. Replica el flujo del switcher (setActive + limpia caché IDB +
// recarga completa) para no arrastrar datos de la sucursal anterior.
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useWindowSize } from '@vueuse/core';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { DataManager } from 'dashboard/helper/CacheHelper/DataManager';

const store = useStore();
const { width } = useWindowSize();
const isMobile = computed(() => width.value < 768);

const { accessibleBranchIds, canAccessBranch } = usePermissions();

const allBranches = computed(() => store.getters['branches/getBranches'] || []);
const branches = computed(() => {
  if (accessibleBranchIds.value.length === 0) return allBranches.value;
  return allBranches.value.filter(b => canAccessBranch(b.id));
});
const activeId = computed(() => store.getters['branches/getActiveBranchId']);

const show = computed(() => isMobile.value && branches.value.length > 1);

const chipLabel = b => b.code || b.name;
const initial = b => (b.name || '?').trim().slice(0, 1).toUpperCase();

async function select(b) {
  if (b.id === activeId.value || !canAccessBranch(b.id)) return;
  store.dispatch('branches/setActive', b.id);
  const accountId = store.getters.getCurrentAccountId;
  // Invalida caché IDB de inboxes/labels (mismo fix que el switcher: el backend
  // no varía cache_key por X-Active-Branch-Id).
  try {
    const dm = new DataManager(accountId);
    await dm.initDb();
    await dm.replace({ modelName: 'inbox', data: [] });
    await dm.replace({ modelName: 'label', data: [] });
  } catch (_) {
    /* IDB no disponible · ignoramos */
  }
  // Recarga completa a Chats con la sucursal nueva (limpia estado en memoria).
  if (accountId) {
    window.location.href = `/app/accounts/${accountId}/dashboard`;
  } else {
    window.location.reload();
  }
}

onMounted(() => {
  if (!allBranches.value.length) store.dispatch('branches/get');
});
</script>

<template>
  <div
    v-if="show"
    class="flex items-center gap-2 px-3 py-2 overflow-x-auto border-b border-n-weak shrink-0"
  >
    <button
      v-for="b in branches"
      :key="b.id"
      type="button"
      class="flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-full text-[11.5px] font-semibold border transition-colors"
      :class="
        b.id === activeId
          ? 'text-[#c9a35a] border-[#c9a35a]/50 bg-[#c9a35a]/10'
          : 'text-n-slate-11 border-n-weak bg-n-alpha-1 hover:text-n-slate-12'
      "
      @click="select(b)"
    >
      <span
        class="flex items-center justify-center w-4 h-4 text-[8px] rounded-full bg-n-alpha-2"
      >{{ initial(b) }}</span>
      {{ chipLabel(b) }}
    </button>
  </div>
</template>
